import { useCallback, useEffect, useState } from 'react'
import { DndProvider as ReactDndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { Navigate, Route, Routes } from 'react-router-dom'
import { useAtom } from 'jotai'

import { MtwProvider } from '@makyoapp/makyo-tw-ui'
import { ModalNotification } from '@makyoapp/makyo-ui'

import { toolbarAtom } from '@/lib/jotai'
import { appConfig } from '@/configs'
import { ActiveLayerProvider } from '@/context/__project__/ActiveLayerContext'
import { AppAuthProvider, useAppAuthContext } from '@/context/__project__/AppAuthContext'
import { AppRoutingProvider } from '@/context/__project__/AppRoutingContext'
import { BuilderAccordionProvider } from '@/context/__project__/BuilderAccordionContext'
import { BuilderActionProviderV2 } from '@/context/__project__/BuilderActionContextV2'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ActionFormProviderV2 } from '@/context/ActionFormProviderV2'
import { useAppBrandKitContext } from '@/context/AppBrandKitContext'
import { useAppBuilderContext } from '@/context/AppBuilderContext'
import { AppOverlaysProvider } from '@/context/AppOverlaysContext'
import { AppPagesProvider } from '@/context/AppPagesContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { UndoRedoProvider, useUndoRedoContext } from '@/context/UndoRedoContext'
import { useBuilderColorSchemeV2 } from '@/hooks/__project__/useBuilderColorSchemeV2'
import { useFontLink } from '@/hooks/__project__/useFontLink'
import useAuth from '@/hooks/useAuth'
import { useBreakpoint } from '@/hooks/useBreakpoint'
import { useHotUpdateDetector } from '@/hooks/useHotUpdateDetector'
import { UserDropdownV2 } from '@/components/UserDropdownV2'

import { AppBuilderDevTools } from '../devtools/AppBuilderDevTools'
import { UndoManagerDevtools } from '../devtools/UndoManagerDevTools'
import { ProjectSettingDropdown, RedoUndo } from '../ProjectToolbar'
import AppPreview from './AppPreview'
import BuilderModals from './BuilderModals'
import BuilderEditor from './Editor'
import BuilderNavigator from './Navigator'

const useInjectToolbar = () => {
  const { userTable, languages, language, setLanguage } = useDbClientContext()
  const { tableUsers, userId, handleLoginUser } = useAppAuthContext()

  const [, setToolbar] = useAtom(toolbarAtom)

  // Use the custom undo/redo hook
  const { canUndo, canRedo, handleUndo, handleRedo } = useUndoRedoContext()

  // inject project setting & user dropdown into toolbar
  useEffect(
    function injectToolbar() {
      setToolbar({
        showDevices: true,
        components: [
          {
            path: '/app',
            component: <RedoUndo canUndo={canUndo} canRedo={canRedo} onUndo={handleUndo} onRedo={handleRedo} />,
          },
          {
            path: '/app',
            component: (
              <ProjectSettingDropdown languages={languages} activeLanguage={language} onLanguageChange={setLanguage} />
            ),
          },
          {
            path: '/app',
            component: userTable ? (
              <div className='h-full min-w-[200px]'>
                <UserDropdownV2
                  user={userId}
                  setUser={handleLoginUser}
                  options={tableUsers}
                  toggleClassName='h-full rounded-none!'
                  className='h-full'
                  noDefaultToggleStyle
                />
              </div>
            ) : null,
          },
        ],
      })

      return () => {
        setToolbar({
          showDevices: false,
          components: [],
        })
      }
    },
    [
      handleLoginUser,
      handleRedo,
      handleUndo,
      canUndo,
      canRedo,
      language,
      languages,
      setLanguage,
      setToolbar,
      tableUsers,
      userId,
      userTable,
    ]
  )
}

/**
 * App Builder v2 main layout
 *
 * @todo: todo: implement collab client
 */
function AppBuilderLayout() {
  const { newVersionAvailable, onClose, refreshPage, lastVersion } = useHotUpdateDetector()

  // Access (makyo) platform auth
  const { userData } = useAuth()

  // Access org
  const { currentOrg } = useCurrentOrgContext()
  const currentOrgId = currentOrg?.id || ''

  // access brandkit for mtw provider
  const { brandkitSourceColor, brandkitExtendedColors, brandkitTypography } = useAppBrandKitContext()
  const [colorScheme] = useBuilderColorSchemeV2()

  // get the font link from brandkit typography
  const { fontLink } = useFontLink(brandkitTypography)

  // Editor & Navigator show state
  const [showEditor, setShowEditor] = useState(true)
  const [showNavigator, setShowNavigator] = useState(true)

  const { isDesktop, isTV } = useBreakpoint()

  const isFullScreen = !showEditor && !showNavigator && (isDesktop || isTV)

  // Get undo/redo handlers for keyboard shortcuts
  const { handleUndo, handleRedo } = useUndoRedoContext()

  const handleKeyDown = useCallback(
    async (event: KeyboardEvent) => {
      const isCtrlOrCmd = event.ctrlKey || event.metaKey // Check for Ctrl (Windows) or Cmd (macOS)

      // Ctrl/Cmd + Arrow Left to toggle editor
      if (event.code === 'ArrowLeft' && isCtrlOrCmd && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        setShowEditor((prev) => !prev)
      }

      // Ctrl/Cmd + Arrow Right to toggle navigator
      if (event.code === 'ArrowRight' && isCtrlOrCmd && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        setShowNavigator((prev) => !prev)
      }

      // Ctrl/Cmd + Arrow Down to toggle both
      if (event.code === 'ArrowDown' && isCtrlOrCmd && !event.altKey && !event.shiftKey) {
        event.preventDefault()
        setShowEditor((prev) => !prev)
        setShowNavigator((prev) => !prev)
      }

      // Cmd/Ctrl + Z for undo
      if (event.key === 'z' && isCtrlOrCmd && !event.shiftKey && !event.altKey) {
        event.preventDefault()
        await handleUndo()
      }

      // Cmd/Ctrl + Shift + Z or Cmd/Ctrl + Y for redo
      if (
        (event.key === 'z' && isCtrlOrCmd && event.shiftKey && !event.altKey) ||
        (event.key === 'y' && isCtrlOrCmd && !event.shiftKey && !event.altKey)
      ) {
        event.preventDefault()
        await handleRedo()
      }
    },
    [handleUndo, handleRedo]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleKeyDown])

  // todo: collab client
  // useCollabClient()

  // inject project setting & user dropdown into toolbar
  useInjectToolbar()

  const handleViewChangelog = () => {
    window.open(appConfig.makyo.CHANGE_LOG_URL, '_blank')
  }

  return (
    <MtwProvider
      ctxDocument={document}
      sourceColor={brandkitSourceColor}
      extendedColors={brandkitExtendedColors}
      mode={colorScheme.current}
      typography={brandkitTypography}
    >
      <AppPagesProvider>
        <AppOverlaysProvider>
          <ActiveLayerProvider>
            <ActionFormProviderV2>
              <BuilderAccordionProvider>
                {/* inject font link to the document head */}
                {fontLink && <link href={fontLink} type='text/css' rel='stylesheet' />}

                {/* main layout */}
                <div className='mk-builder h-[calc(100%-48px)]'>
                  <div className='mk-builder-inner relative flex h-full'>
                    <ReactDndProvider backend={HTML5Backend} context={window}>
                      {showEditor && <BuilderEditor />}
                      <BuilderActionProviderV2 currentOrgId={currentOrgId}>
                        <AppPreview isFullScreen={isFullScreen} />
                      </BuilderActionProviderV2>
                    </ReactDndProvider>
                    {showNavigator && <BuilderNavigator />}
                  </div>
                  <BuilderModals />
                </div>

                {/* modal notification */}
                <ModalNotification
                  show={newVersionAvailable}
                  onClose={onClose}
                  onClick={refreshPage}
                  firstName={userData?.name}
                  onViewChangelog={handleViewChangelog}
                  version={lastVersion}
                  description={
                    <>
                      <span className='font-semibold'>🚀 Makyo v{lastVersion} is here!</span>
                      <br />
                      <span className='inline-block mt-2'>
                        We&apos;ve rolled out powerful updates and improvements. Publish your app today so your users
                        can experience all the new changes right away.
                      </span>
                    </>
                  }
                />

                {/* devtools */}
                <UndoManagerDevtools />
                <AppBuilderDevTools />
              </BuilderAccordionProvider>
            </ActionFormProviderV2>
          </ActiveLayerProvider>
        </AppOverlaysProvider>
      </AppPagesProvider>
    </MtwProvider>
  )
}

/**
 * App Builder v2 layout wrapper
 */
function LayoutWrapper({ children }: { children: React.ReactNode }) {
  return (
    <AppRoutingProvider rootPath='/app'>
      <AppAuthProvider>
        <UndoRedoProvider>{children}</UndoRedoProvider>
      </AppAuthProvider>
    </AppRoutingProvider>
  )
}

/**
 * App Builder v2 layout routes
 */
export default function ProjectLayout() {
  const { pages } = useAppBuilderContext()

  // redirect to first page if exist
  const initialPageSlug = pages?.find((p) => p.slug === 'home' || !!p.slug)?.slug ?? ''

  return (
    <Routes>
      <Route index element={<Navigate to={initialPageSlug} replace />} />
      <Route
        path=':slug?/:rowId?'
        element={
          <LayoutWrapper>
            <AppBuilderLayout />
          </LayoutWrapper>
        }
      />
    </Routes>
  )
}
